import React, { useState, useRef } from "react";
import { View } from "react-native";
import { useRouter } from "expo-router";
import { CreateHeader } from "@/components/screens/shared";
import useToast from "@/hooks/useToast";
import { incidentService } from "@/services/incidentService";
import useAuth from "@/hooks/useAuth";
import Head from "expo-router/head";
import { useRowSelectionContext } from "@/providers/RowSelectionProvider";
import { useTheme } from "@/hooks/useTheme";
import { getSaveBehavior } from "@/lib/saveSettings";
import {
  IncidentCreateForm,
  IncidentCreateFormRef,
  IncidentPayload,
  INCIDENT_STATUS_OPTIONS,
  getIncidentStatusColor,
} from "@/components/screens/incident/NewIncidentForm";

export { INCIDENT_STATUS_OPTIONS, getIncidentStatusColor } from "@/components/screens/incident/NewIncidentForm";

export default function NewIncidentPage() {
  const toast = useToast();
  const router = useRouter();
  const { colors } = useTheme();
  const { tenantId } = useAuth();
  const { triggerRefresh } = useRowSelectionContext();

  const [name, setName] = useState("");
  const [status, setStatus] = useState("PENDING REVIEW");
  const [subtitle, setSubtitle] = useState<string | undefined>();
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const formRef = useRef<IncidentCreateFormRef>(null);

  const handleCreate = async (payload: IncidentPayload) => {
    if (!tenantId) {
      toast.showToast({
        title: "Error",
        message: "Tenant ID not found",
        toastType: "error",
      });
      return;
    }

    setSaving(true);
    try {
      const { incident } = payload;

      const newIncident = await incidentService.addIncident({
        tenant_id: tenantId,
        name: incident.name.trim(),
        description: incident.description?.trim() || null,
        status: incident.status,
        meta_data: {
          incident_type_id: incident.incident_type_id,
          submitted_by: incident.submitted_by_id,
          occurred_date: incident.occurred_date,
          ...(incident.client_id && {
            client_id: incident.client_id,
          }),
          ...(incident.project_id && {
            project_id: incident.project_id,
          }),
          ...(incident.shift_id && {
            shift_id: incident.shift_id,
          }),
          ...(incident.personnel_involved.length > 0 && {
            personnel_involved: incident.personnel_involved,
          }),
          ...(incident.contacts_involved.length > 0 && {
            contacts_involved: incident.contacts_involved,
          }),
          ...(incident.location_id && {
            location_id: incident.location_id,
          }),
        },
      });

      toast.showToast({
        title: "Incident Created",
        message: "Incident report has been created successfully",
        toastType: "success",
      });

      triggerRefresh("incident");

      // Handle save behavior setting
      const behavior = await getSaveBehavior("incident");
      switch (behavior) {
        case "view_created":
          router.setParams({ new: undefined, id: newIncident?.id });
          break;
        case "create_another":
          handleReset();
          break;
        case "close":
        default:
          router.setParams({ new: undefined, panel: undefined });
          break;
      }
    } catch (err: any) {
      toast.showToast({
        title: "Failed to Create Incident",
        message: err.message || "An error occurred while creating the incident",
        toastType: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setName("");
    setStatus("PENDING REVIEW");
    setSubtitle(undefined);
    formRef.current?.handleReset();
  };

  return (
    <>
      <Head>
        <title>NEXUS New Incident</title>
        <meta name="description" content="Create a new incident report" />
      </Head>
      <View style={{ flex: 1 }}>
        <CreateHeader
          entityType="incident"
          name={name}
          onNameChange={(n) => {
            setName(n);
            formRef.current?.updateIncidentName(n);
          }}
          namePlaceholder="Incident name"
          subtitle={subtitle}
          dirty={dirty}
          saving={saving}
          onSave={() => formRef.current?.handleSave()}
          onCancel={handleReset}
          status={status}
          onStatusChange={(s) => {
            setStatus(s);
            formRef.current?.updateIncidentStatus(s);
          }}
          statusOptions={INCIDENT_STATUS_OPTIONS.map((opt) => ({
            text: opt.text,
            bgColor: getIncidentStatusColor(opt.text, colors),
          }))}
        />
        <View style={{ flex: 1, padding: 16 }}>
          <IncidentCreateForm
            ref={formRef}
            initialIncident={{ name, status }}
            onChange={() => {}}
            onSave={handleCreate}
            onDirtyChange={setDirty}
            onSubtitleChange={setSubtitle}
          />
        </View>
      </View>
    </>
  );
}
